import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, X, FileText, Download, ArrowRight, PlusCircle, CheckCircle2, Send, XCircle, Clock, Receipt, ExternalLink, GitBranch, History, Lock, Save, Pencil, ShieldCheck, Package, Search } from 'lucide-react';
import QuotationEditSessionModal from '../components/QuotationEditSessionModal';
import RevisionExplainerCard from '../components/RevisionExplainerCard';
import QuotationApprovalBanner from '../components/QuotationApprovalBanner';
import ApprovalChainTimeline from '../components/ApprovalChainTimeline';
import PricingRecommendationCard from '../components/PricingRecommendationCard';
import VelocityAlertsStrip from '../components/VelocityAlertsStrip';
import ContractRiskCard from '../components/ContractRiskCard';
import RoutingDecisionCard from '../components/RoutingDecisionCard';
import VendorQuoteImportModal from '../components/VendorQuoteImportModal';
import UnifiedQuotationTimeline from '../components/UnifiedQuotationTimeline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

// Helper function to process Arabic text for proper display in PDF
// Arabic text needs to be reshaped (connected letters) for correct display
// Note: With proper Arabic fonts like NotoSansArabic, NO reversal is needed
const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  
  try {
    // Reshape Arabic letters to connect them properly
    // The font handles RTL direction automatically
    return convertArabic(text);
  } catch (e) {
    console.error('Arabic text processing error:', e);
    return text;
  }
};

const Quotations = ({ user }) => {
  const [quotations, setQuotations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  // Patch 18.1.1 — Visibility Stability Layer.
  // Drives the backend `status` query-param: 'all' (default) surfaces
  // both approved + pending so freshly-created drafts show up; the
  // CFO view can switch to 'approved' for finance-only and an
  // operator may pick 'pending' to focus on the in-flight queue.
  const [approvalFilter, setApprovalFilter] = useState('all');
  // Patch 21 — Server-side pagination, search & sort.
  // URL state is sync'd so the user can deep-link and not lose their
  // place when they hop into a Timeline drawer and come back via the
  // browser back button.
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('quotation_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pageMeta, setPageMeta] = useState({ total: 0, total_pages: 0 });
  const [converting, setConverting] = useState(false);
  // Governance contract — fetched from the server per-selected quotation.
  // The UI MUST drive button visibility from this, never from a
  // hard-coded role list. Server stays the source of truth.
  const [governance, setGovernance] = useState(null);
  // Versioned Commercial Contract — revision chain for selected quotation
  const [revisions, setRevisions] = useState(null);
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);
  // Multi-Step Approval Chain — current chain (or null) for selected quotation
  const [approvalChain, setApprovalChain] = useState(null);
  // Controlled Mutation Layer — Edit Session modal toggle.
  const [showEditSession, setShowEditSession] = useState(false);
  // Vendor Quotation import flow inside +New Quotation modal.
  const [showVendorImport, setShowVendorImport] = useState(false);
  // Patch 18.1 — Unified Quotation Timeline (Decision Narrative) drawer.
  const [showTimeline, setShowTimeline] = useState(false);

  // Optimistic guard for users who would never have permission anyway —
  // hides the convert button entirely (per spec: hidden, not disabled).
  const ROLE_CAN_CONVERT = user?.role === 'admin' || user?.role === 'accountant';
  const ROLE_CAN_REVISE  = user?.role === 'admin' || user?.role === 'accountant' || user?.role === 'sales_rep';
  
  // Check if user can delete a quotation
  const canDelete = (quotation) => {
    if (user?.role === 'admin') return true;
    return quotation.status === 'draft';
  };
  
  const defaultTerms = `SECTION 1: PROPOSAL DEVIATIONS & EXCLUSIONS
We attempted to incorporate all your scope & technical requirements as obtained from you. If we have made any assumptions, deviated from your exact requirements, or excluded any item from our supply, we should have noted them in this section. Please help us by reading this proposal thoroughly and giving us your feedback to ensure that we have not overlooked anything.
As a general rule, the following are always excluded from our scope:
1. Our price does not include any provision for maintenance works.
2. The work does not include any civil work Electrical wiring etc.
3. Permanent power supply of 230/380V must be provided by the buyer at the nearest location.
4. The temporary power supply of 220V/380V must be provided by the buyer to the working site for installation.
5. No allowance has been made for gate passes (if any).
6. No allowance has been made to design, detail, coordinate or build any substructure supporting system.
7. Any provision for inspection and special testing (other than our standard QA & QC) is excluded from our scope and will be charged extra if required. Payment should not be held because of testing.

SECTION 2: INSTALLATION SCOPE
Our scope of Installation includes only the direct Installation activities of materials we supplied and such activities as detailed herein below.
1. Responsible for material transfer to the site.
2. Storing appropriately our materials at the job site in the storage area provided by the Customer during the period when Installation work is in progress.
3. Installing our materials in strict compliance with our Installation drawings and guidelines.
4. Providing insurance at the job site throughout the Installation period. This includes Workmen Compensation Insurance, Installation All Risk Insurance covering third-party liability, and insurance for Equipment and Vehicles used by us for Installation.
5. Disposal of the waste, and garbage off-site related to the executed scope.

SECTION 3: STANDARD TERMS & CONDITIONS OF INSTALLATION
1. Acceleration: our plans, schedules, and identifies the cost of the resources on-site at the time of submission of this proposal offer based on complete and clear access at all times. Any change in site condition, accessibility concerns arising after this date, or acceleration required by Customer for any reason, may result in a revision of price and no additional resources shall be deployed or Installation methodology be changed unless the Customer and we agree on the revised price and schedule.
2. Change Orders: In addition to change orders/variations, any Customer requested change or modification to the buildings that contradicts the details on our Installation drawings, shall be considered as a variation and will be carried out only after the Parties agree upon the terms and conditions for such variation including price and schedule.
3. We recycle all our wood packing material (dunnage and pallets). The Customer/Contractor must allow us to remove such material from the site.

SECTION 4: WARRANTY
1. Warranty: The number of Year/s shall be discussed and shall be from the date of Installation.
2. Any accidents or unprofessional manhandling are not covered under warranty.

SECTION 5: CONTRACTOR/CLIENT RESPONSIBILITY
1. Access around the building for unloading of material: The Customer/Contractor must ensure that the footprint of all buildings is free for unloading of material with requisite access.
2. Providing Installation equipment Lifting Equipment, Ladders, Scaffoldings, and Man lift.
3. Protection of materials: If the materials are shipped before site readiness to start the Installation, the Customer/Contractor shall be responsible to protect our supplied materials from mechanical and environmental damage after the delivery to the job site till the start of Installation work.
4. Damage of materials by other contractors: Our material that is damaged by other contractors, before, during, and after installation, shall be replaced or repaired on the Customer account. Additional Installation time will be claimed by us for delays due to such negligence.
5. Suspension of Work: Our Installation price is based on a single mobilization cost. If for any reason, the Customer instructs us to suspend the work at any point in time for a period longer than 2 (Two) days, we reserve the right to charge extra cost for remobilization.
6. Stoppage for short intervals: If Installation is stopped for short intervals of time (days) due to actions by the Customer causing our labor and equipment to be idle at the site, idle charges will be charged to the Customer for each day of stoppage.
7. Termination: If the Contract is terminated for any reason, we shall be entitled to forthwith payment of all outstanding dues and dues for work undertaken till the date of such termination.
8. If the Installation works fall during Ramadan/summer season/public holidays, our Installation period may be affected and revised accordingly.
9. Provision of Utility like water & electricity needed for installation (if required).

SECTION 6: PROJECT SCHEDULE
1. Start mobilization a week before the material is delivered to the site.
2. Completion as per project schedule.`;

  const [formData, setFormData] = useState({
    quotation_date: new Date().toISOString().split('T')[0],
    valid_date: '',
    customer_id: '',
    attention_to: '',
    project_id: '',
    subject: '',
    items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0, inventory_id: '' }],
    subtotal: 0,
    vat_rate: 15,
    vat_amount: 0,
    total: 0,
    payment_terms: '',
    delivery_terms: '',
    terms: defaultTerms,
    notes: ''
  });

  const [inventory, setInventory] = useState([]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Patch 18.1.1 / Patch 21 — refetch when any list-driving knob
  // changes so the operator sees fresh data without a manual reload.
  // Skipping the first render avoids a redundant double-fetch on mount.
  const isFirstFilterRender = React.useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    fetchData();
  }, [approvalFilter, page, searchQuery, sortBy, sortOrder]);

  // Patch 21 — URL ↔ state sync so /quotations?page=3&search=Latifia
  // restores the operator's exact spot even after a Timeline drawer
  // round-trip or a browser back/forward. Mounted-once for query-
  // string seeding; subsequent writes happen via replaceState so we
  // don't pollute history.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.has('page'))       setPage(parseInt(sp.get('page'), 10) || 1);
    if (sp.has('search'))   { setSearchQuery(sp.get('search')); setSearchInput(sp.get('search')); }
    if (sp.has('status'))     setApprovalFilter(sp.get('status'));
    if (sp.has('sort_by'))    setSortBy(sp.get('sort_by'));
    if (sp.has('sort_order')) setSortOrder(sp.get('sort_order'));
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams();
    if (page > 1)                sp.set('page', String(page));
    if (searchQuery)             sp.set('search', searchQuery);
    if (approvalFilter !== 'all') sp.set('status', approvalFilter);
    if (sortBy !== 'quotation_date') sp.set('sort_by', sortBy);
    if (sortOrder !== 'desc')    sp.set('sort_order', sortOrder);
    const qs = sp.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    if (next !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, '', next);
    }
  }, [page, searchQuery, approvalFilter, sortBy, sortOrder]);

  // Governance contract — refresh whenever the selected quotation
  // changes. Drives Convert button visibility + Edit lock state.
  useEffect(() => {
    if (!selectedQuotation?.id) {
      setGovernance(null);
      setRevisions(null);
      setApprovalChain(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [gov, chain, chainRow] = await Promise.all([
          axiosInstance.get(`/quotations/${selectedQuotation.id}/governance`),
          axiosInstance.get(`/quotations/${selectedQuotation.id}/revisions`).catch(() => null),
          axiosInstance.get(`/quotations/${selectedQuotation.id}/approval-chain`).catch(() => ({ data: { chain: null } })),
        ]);
        if (cancelled) return;
        setGovernance(gov.data);
        setRevisions(chain?.data || null);
        setApprovalChain(chainRow?.data?.chain || null);
      } catch (_) {
        if (!cancelled) {
          setGovernance(null);
          setRevisions(null);
          setApprovalChain(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedQuotation?.id, selectedQuotation?.status]);

  // Versioned Commercial Contract — fork a new revision off the
  // currently-selected quotation. Server is the source of truth on
  // what's allowed; the UI hides this button when the contract says no.
  const handleRevise = async (q) => {
    if (!q) return;
    const reason = window.prompt(
      `Create a new revision of ${q.quotation_no}?\n\n` +
      `This will be R${(q.revision_number || 0) + 1}. The current ` +
      `version becomes a historical snapshot.\n\n` +
      `Reason (optional):`,
      ''
    );
    if (reason === null) return;  // user cancelled
    try {
      // Forward the current items + totals as the starting point —
      // user will edit the new draft in place.
      const { data } = await axiosInstance.post(
        `/quotations/${q.id}/revise`,
        {
          items: q.items || [],
          subject: q.subject,
          quotation_date: q.quotation_date,
          valid_date: q.valid_date,
          notes: q.notes,
          terms: q.terms,
          vat_rate: q.vat_rate,
          revision_reason: reason || undefined,
        }
      );
      toast.success(`Revision ${data.quotation_no} created — opened as draft`);
      // Switch the detail panel to the new revision.
      setSelectedQuotation(data);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        toast.error('Your role is not allowed to revise quotations');
      } else if (err.response?.status === 409) {
        toast.error(typeof detail === 'object' ? (detail.message || detail.error) : 'Cannot revise');
      } else {
        toast.error('Failed to create revision');
      }
    }
  };

  const fetchData = async () => {
    try {
      const qParams = new URLSearchParams({
        status: approvalFilter,
        page: String(page),
        page_size: String(PAGE_SIZE),
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      if (searchQuery) qParams.set('search', searchQuery);
      const results = await Promise.allSettled([
        axiosInstance.get(`/quotations?${qParams.toString()}`),
        axiosInstance.get('/customers'),
        axiosInstance.get('/projects'),
        axiosInstance.get('/inventory')
      ]);
      const labels = ['quotations', 'customers', 'projects', 'inventory'];
      const setters = [
        // Patch 21 envelope handler — accept either {items,…} (new
        // shape) or a flat array (defensive fallback in case the
        // service-worker cache returns the legacy response).
        (data) => {
          if (Array.isArray(data)) {
            setQuotations(data);
            setPageMeta({ total: data.length, total_pages: 1 });
          } else {
            setQuotations(data.items || []);
            setPageMeta({
              total:       data.total       || 0,
              total_pages: data.total_pages || 0,
            });
          }
        },
        setCustomers, setProjects, setInventory,
      ];
      const failed = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          setters[i](r.value.data);
        } else {
          failed.push(labels[i]);
          console.error(`Failed to load /${labels[i]}:`, r.reason?.response?.status, r.reason?.message);
        }
      });
      if (failed.length && loading) {
        toast.error(`Failed to load: ${failed.join(', ')}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customer_id: customerId,
      attention_to: customer?.attention_to || ''
    });
  };

  const calculateTotals = (items, vatRate) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    
    // If selecting inventory item, auto-fill description, unit, and unit_price
    if (field === 'inventory_id') {
      const selectedItem = inventory.find(inv => inv.id === value);
      if (selectedItem) {
        newItems[index] = {
          ...newItems[index],
          inventory_id: value,
          description: selectedItem.name || selectedItem.item_name,
          unit: selectedItem.unit || 'unit',
          unit_price: selectedItem.unit_price || 0,
          total: (newItems[index].quantity || 1) * (selectedItem.unit_price || 0)
        };
      } else {
        newItems[index].inventory_id = value;
        newItems[index].description = '';
      }
    } else {
      newItems[index][field] = value;
      
      if (field === 'quantity' || field === 'unit_price') {
        const qty = parseFloat(newItems[index].quantity) || 0;
        const price = parseFloat(newItems[index].unit_price) || 0;
        newItems[index].total = qty * price;
      }
    }
    
    const { subtotal, vatAmount, total } = calculateTotals(newItems, formData.vat_rate);
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      vat_amount: vatAmount,
      total
    });
  };

  const handleVatChange = (vatRate) => {
    const { subtotal, vatAmount, total } = calculateTotals(formData.items, vatRate);
    setFormData({
      ...formData,
      vat_rate: vatRate,
      vat_amount: vatAmount,
      total
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0, inventory_id: '' }]
    });
  };

  /**
   * handleVendorImport — append vendor-quotation lines into the active
   * customer quotation draft.
   *
   * The picker returns items already shaped to match our customer-side
   * schema (description, quantity, unit, unit_price, total). We strip
   * any empty/blank starter row so the user doesn't have to delete it
   * manually after pulling 12 lines in.
   */
  const handleVendorImport = (importedItems) => {
    if (!importedItems || importedItems.length === 0) return;
    setFormData((prev) => {
      // Drop the placeholder first row if it's still empty.
      const existing = (prev.items || []).filter(
        (li) => (li.description || '').trim() !== '' || (li.unit_price || 0) > 0 || (li.inventory_id || '') !== ''
      );
      const merged = [...existing, ...importedItems];
      const { subtotal, vatAmount, total } = calculateTotals(merged, prev.vat_rate);
      return {
        ...prev,
        items: merged,
        subtotal,
        vat_amount: vatAmount,
        total,
      };
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    const { subtotal, vatAmount, total } = calculateTotals(newItems, formData.vat_rate);
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      vat_amount: vatAmount,
      total
    });
  };

  const [saving, setSaving] = useState(false);

  /**
   * handleSubmit — the modal's primary action.
   *
   * Two routes, driven by ``sendAfterSave``:
   *   • false → "Save as Draft": POST /quotations, stays in DRAFT
   *     status. The user can re-open the draft from the list and
   *     keep editing it (governance allows edits while status=draft
   *     AND is_latest=true). This is the "draft re-entry" workflow
   *     the user explicitly requested.
   *   • true  → "Save & Send to Customer": POST then immediately
   *     transition to SENT, so the quotation becomes a real shared
   *     contract (and any further changes must go through Revise).
   *
   * Server is the source of truth on every step — the UI just picks
   * which buttons to render.
   */
  const handleSubmit = async (e, { sendAfterSave = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();

    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (formData.items.some(item => !item.description || item.total === 0)) {
      toast.error('Please fill all item details');
      return;
    }

    setSaving(true);
    try {
      const response = await axiosInstance.post('/quotations', formData);
      const q = response.data;

      if (sendAfterSave) {
        // Promote the draft to a sent contract in one click.
        try {
          await axiosInstance.post(`/quotations/${q.id}/transition?new_status=sent`);
          toast.success(`Quotation ${q.quotation_no} sent to customer`);
        } catch (_) {
          toast.success(`Quotation ${q.quotation_no} saved (sending failed — retry from the detail panel)`);
        }
      } else if (q.approval_status === 'pending') {
        toast.success('Saved as draft — submitted for approval', {
          description: 'You can re-open this draft from the list and keep editing before sending.',
        });
      } else {
        toast.success(`Saved as draft — ${q.quotation_no}`, {
          description: 'Re-open from the list to keep editing before sending to the customer.',
        });
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axiosInstance.put(`/quotations/${id}/status?status=${status}`);
      toast.success(`Quotation status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // State-machine transition: enforces DRAFT → SENT → ACCEPTED with
  // terminal guards (rejected/expired/converted). Backend rejects illegal
  // transitions with 409 — we surface that to the user.
  const STATE_MACHINE = {
    draft:     ['sent', 'rejected', 'expired'],
    sent:      ['accepted', 'rejected', 'expired'],
    accepted:  ['rejected', 'expired'],   // converted handled by Convert button
    converted: [],
    rejected:  [],
    expired:   [],
  };

  const transitionTo = async (q, newStatus) => {
    try {
      const { data } = await axiosInstance.post(
        `/quotations/${q.id}/transition?new_status=${newStatus}`
      );
      toast.success(`Moved to ${newStatus.toUpperCase()}`);
      // backend returns the updated row; reflect immediately.
      if (data && data.id) {
        setSelectedQuotation(data);
      }
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        const msg = typeof detail === 'object'
          ? `Cannot move from ${detail.from} to ${detail.to}. Allowed: ${(detail.allowed || []).join(', ') || '—'}`
          : 'Transition not allowed';
        toast.error(msg);
      } else {
        toast.error('Failed to update status');
      }
    }
  };

  const handleConvertToInvoice = async (q) => {
    if (!window.confirm(
      `Convert quotation ${q.quotation_no} to an invoice? ` +
      `An invoice for ${q.total?.toLocaleString()} SAR will be created and ` +
      `linked back to this quotation for full traceability.`
    )) {
      return;
    }
    setConverting(true);
    try {
      const { data } = await axiosInstance.post(`/quotations/${q.id}/convert-to-invoice`);
      if (data.already_converted) {
        toast.info(`Already converted to ${data.invoice_number}`);
      } else {
        toast.success(`Invoice ${data.invoice_number} created — totals: ${data.totals?.total?.toLocaleString()} SAR`);
      }
      // Refresh the selected quotation to pick up the converted_invoice_* fields.
      try {
        const refreshed = await axiosInstance.get(`/quotations/${q.id}`);
        setSelectedQuotation(refreshed.data);
      } catch (_) { /* ignore */ }
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.error(
          typeof detail === 'object'
            ? `Cannot convert: ${detail.error || detail.status || 'terminal state'}`
            : 'Cannot convert this quotation'
        );
      } else {
        toast.error('Failed to convert to invoice');
      }
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      try {
        await axiosInstance.delete(`/quotations/${id}`);
        toast.success('Quotation deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete quotation');
      }
    }
  };

  const downloadPDF = async (quotation) => {
    const doc = new jsPDF();
    
    // Add Arabic font to jsPDF
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');
    
    // Add Company Logo - same as Invoice (width 52, height 30)
    try {
      const logoResponse = await fetch('/logo.png');
      if (logoResponse.ok) {
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoBase64, 'PNG', 10, 5, 52, 30);
      }
    } catch (err) {
      console.error('Logo load failed:', err);
    }
    
    // Company Header - Arabic Name using Arabic font (right aligned)
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(10, 66, 117);
    doc.text(processArabicText('شركة الطريق العالي المتميز'), 190, 10, { align: 'right' });
    
    // English company name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Distinct High Road', 190, 16, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Hafsa Bint Omar Street, AlRawdah Dist, Riyadh, KSA', 190, 22, { align: 'right' });
    doc.text('VAT: 314477769800003 | CR: 7052942476', 190, 27, { align: 'right' });
    doc.text('Tel: +966552168158 | Email: Info@dhr-sa.com', 190, 32, { align: 'right' });
    
    // Horizontal line
    doc.setDrawColor(10, 66, 117);
    doc.setLineWidth(0.5);
    doc.line(20, 38, 190, 38);
    
    // Quotation Title (centered)
    doc.setFontSize(18);
    doc.setTextColor(10, 66, 117);
    doc.setFont('helvetica', 'bold');
    doc.text('QUOTATION', 105, 48, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    
    // Two columns: Client Details (Left) | Quotation Details (Right)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(10, 66, 117);
    doc.text('Client Details', 20, 58);
    doc.text('Quotation Details', 115, 58);
    
    // Client Details (Left Side)
    doc.setTextColor(0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let clientY = 66;
    
    // Check if customer_name contains Arabic
    const customerName = quotation.customer_name || '';
    const hasArabicCustomerName = /[\u0600-\u06FF]/.test(customerName);
    if (hasArabicCustomerName) {
      doc.text('Client: ', 20, clientY);
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(customerName), 35, clientY);
      doc.setFont('helvetica', 'normal');
    } else {
      doc.text(`Client: ${customerName}`, 20, clientY);
    }
    clientY += 6;
    
    // Add Arabic client name if exists and different
    if (quotation.customer_name_arabic && quotation.customer_name_arabic !== customerName) {
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(quotation.customer_name_arabic), 20, clientY);
      doc.setFont('helvetica', 'normal');
      clientY += 6;
    }
    
    // Address - use Arabic font if needed
    const addressText = quotation.customer_address || 'N/A';
    const hasArabicAddress = /[\u0600-\u06FF]/.test(addressText);
    if (hasArabicAddress) {
      doc.text('Address: ', 20, clientY);
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(addressText), 38, clientY);
      doc.setFont('helvetica', 'normal');
    } else {
      doc.text(`Address: ${addressText}`, 20, clientY);
    }
    clientY += 6;
    
    doc.text(`Attention To: ${quotation.attention_to || 'N/A'}`, 20, clientY);
    clientY += 6;
    doc.text(`CR Number: ${quotation.customer_cr_number || 'N/A'}`, 20, clientY);
    clientY += 6;
    doc.text(`VAT No: ${quotation.customer_vat_number || 'N/A'}`, 20, clientY);
    
    // Quotation Details (Right Side)
    doc.setFontSize(9);
    let qY = 66;
    doc.text(`Quotation No: ${quotation.quotation_no}`, 115, qY);
    qY += 6;
    doc.text(`Date: ${new Date(quotation.quotation_date).toLocaleDateString()}`, 115, qY);
    qY += 6;
    doc.text(`Valid Until: ${new Date(quotation.valid_date).toLocaleDateString()}`, 115, qY);
    qY += 6;
    
    // Project name - handle Arabic
    if (quotation.project_name) {
      const projectName = quotation.project_name;
      const hasArabicProject = /[\u0600-\u06FF]/.test(projectName);
      if (hasArabicProject) {
        doc.text('Project: ', 115, qY);
        doc.setFont('NotoSansArabic', 'normal');
        doc.text(processArabicText(projectName), 132, qY);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.text(`Project: ${projectName}`, 115, qY);
      }
      qY += 6;
    }
    
    // Status
    doc.text(`Status: ${quotation.status?.toUpperCase() || 'DRAFT'}`, 115, qY);
    
    // Subject
    let tableStartY = 108;
    if (quotation.subject) {
      doc.setFontSize(10);
      doc.setTextColor(10, 66, 117);
      doc.setFont('helvetica', 'bold');
      doc.text('Subject:', 20, 100);
      doc.setTextColor(0);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const subjectText = quotation.subject;
      if (/[\u0600-\u06FF]/.test(subjectText)) {
        doc.setFont('NotoSansArabic', 'normal');
        doc.text(processArabicText(subjectText), 40, 100);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.text(subjectText, 40, 100);
      }
      tableStartY = 108;
    }
    
    // Items Table - with Arabic support for descriptions
    // Store which rows have Arabic text for font selection
    const arabicRows = [];
    const tableData = quotation.items.map((item, index) => {
      const desc = item.description || '';
      const hasArabic = /[\u0600-\u06FF]/.test(desc);
      if (hasArabic) {
        arabicRows.push(index);
      }
      return [
        (index + 1).toString(),
        hasArabic ? processArabicText(desc) : desc,
        item.quantity.toString(),
        item.unit || 'unit',
        `${item.unit_price.toFixed(2)} SAR`,
        `${item.total.toFixed(2)} SAR`
      ];
    });
    
    autoTable(doc, {
      startY: tableStartY,
      head: [['#', 'Description', 'Qty', 'Unit', 'Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [10, 66, 117], font: 'helvetica', fontStyle: 'bold' },
      styles: {
        font: 'helvetica',
        fontSize: 9
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { cellWidth: 70 },
        2: { halign: 'center', cellWidth: 15 },
        3: { halign: 'center', cellWidth: 18 },
        4: { halign: 'right', cellWidth: 30 },
        5: { halign: 'right', cellWidth: 30 }
      },
      didParseCell: function(data) {
        // Apply Arabic font to description column for rows with Arabic text
        if (data.section === 'body' && data.column.index === 1) {
          if (arabicRows.includes(data.row.index)) {
            data.cell.styles.font = 'NotoSansArabic';
          }
        }
        // Also check unit column for Arabic units
        if (data.section === 'body' && data.column.index === 3) {
          const unitText = data.cell.raw;
          if (unitText && /[\u0600-\u06FF]/.test(unitText)) {
            data.cell.styles.font = 'NotoSansArabic';
          }
        }
      }
    });
    
    // Helper to add a new page if the next section won't fit
    const PAGE_HEIGHT = doc.internal.pageSize.height;
    const BOTTOM_MARGIN = 20;
    const ensureSpace = (currentY, neededMm) => {
      if (currentY + neededMm > PAGE_HEIGHT - BOTTOM_MARGIN) {
        doc.addPage();
        return 20; // top margin on new page
      }
      return currentY;
    };

    let finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 150;
    // Totals block needs ~20mm
    finalY = ensureSpace(finalY, 22);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Subtotal: ${quotation.subtotal.toFixed(2)} SAR`, 140, finalY);
    doc.text(`VAT (${quotation.vat_rate}%): ${quotation.vat_amount.toFixed(2)} SAR`, 140, finalY + 6);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${quotation.total.toFixed(2)} SAR`, 140, finalY + 14);
    doc.setFont('helvetica', 'normal');
    
    // Payment & Delivery Terms (English only labels)
    let termsY = finalY + 25;
    doc.setFontSize(10);

    // Multi-line writer: wraps to page width, supports Arabic + \n
    const writeTermsBlock = (label, text) => {
      const isAr = /[\u0600-\u06FF]/.test(text);
      const maxW = 135; // 55 → 190 printable width
      if (isAr) doc.setFont('NotoSansArabic', 'normal');
      else doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(text, maxW);
      termsY = ensureSpace(termsY, lines.length * 5 + 6);
      doc.setTextColor(10, 66, 117);
      doc.setFont('helvetica', 'bold');
      doc.text(label, 20, termsY);
      doc.setTextColor(0);
      if (isAr) doc.setFont('NotoSansArabic', 'normal');
      else doc.setFont('helvetica', 'normal');
      lines.forEach((ln, i) => {
        doc.text(isAr ? processArabicText(ln) : ln, 55, termsY + i * 5);
      });
      doc.setFont('helvetica', 'normal');
      termsY += Math.max(lines.length * 5, 5) + 2;
    };

    const paymentTerms = quotation.payment_terms || '50% advance, 50% upon completion';
    writeTermsBlock('Payment Terms:', paymentTerms);

    const deliveryTerms = quotation.delivery_terms || 'As per project schedule';
    writeTermsBlock('Delivery Terms:', deliveryTerms);
    
    // Bank Details (Vertical Layout) - needs ~45mm (header + 4 rows)
    let bankY = termsY + 8;
    bankY = ensureSpace(bankY, 45);
    doc.setFontSize(11);
    doc.setTextColor(10, 66, 117);
    doc.setFont('helvetica', 'bold');
    doc.text('Bank Details', 20, bankY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0);
    doc.setFontSize(9);
    
    const bankDataVertical = [
      ['Bank Name', 'ALAHLI BANK (SNB)'],
      ['Account', '24700000524008'],
      ['IBAN', 'SA6910000024700000524008'],
      ['SWIFT', 'NCBKSAJE']
    ];
    
    autoTable(doc, {
      startY: bankY + 5,
      body: bankDataVertical,
      theme: 'striped',
      columnStyles: {
        0: { cellWidth: 35, fontStyle: 'bold', fillColor: [10, 66, 117], textColor: [255, 255, 255] },
        1: { cellWidth: 60 }
      },
      styles: { fontSize: 9 }
    });
    
    // Terms & Conditions after Bank Details - Always use full default terms
    const fullTerms = defaultTerms;
    let conditionsY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 200;
    const pageHeight = doc.internal.pageSize.height;
    
    // Check if we need a new page
    if (conditionsY > pageHeight - 50) {
      doc.addPage();
      conditionsY = 20;
    }
    
    doc.setFontSize(11);
    doc.setTextColor(10, 66, 117);
    doc.text('Terms & Conditions', 20, conditionsY);
    doc.setTextColor(0);
    doc.setFontSize(8);
    
    const termsLines = doc.splitTextToSize(fullTerms, 170);
    let yPos = conditionsY + 8;
    
    termsLines.forEach((line) => {
      if (yPos > pageHeight - 15) {
        doc.addPage();
        yPos = 20;
      }
      // Check if line is a section header
      if (line.startsWith('SECTION')) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(line, 20, yPos);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
      } else {
        doc.text(line, 20, yPos);
      }
      yPos += 4;
    });
    
    // Download PDF directly
    doc.save(`Quotation-${quotation.quotation_no}.pdf`);
    toast.success('PDF downloaded');
  };

  const resetForm = () => {
    setFormData({
      quotation_date: new Date().toISOString().split('T')[0],
      valid_date: '',
      customer_id: '',
      attention_to: '',
      project_id: '',
      subject: '',
      items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0, inventory_id: '' }],
      subtotal: 0,
      vat_rate: 15,
      vat_amount: 0,
      total: 0,
      payment_terms: '',
      delivery_terms: '',
      terms: defaultTerms,
      notes: ''
    });
    setSelectedCustomer(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'planning',
      sent: 'in_progress',
      accepted: 'completed',
      converted: 'completed',
      rejected: 'overdue',
      expired: 'on_hold'
    };
    return colors[status] || 'planning';
  };

  // Apply the status-filter chip selection on top of the raw list.
  const filteredQuotations = statusFilter === 'all'
    ? quotations
    : quotations.filter(q => (q.status || 'draft') === statusFilter);

  const statusCounts = quotations.reduce((acc, q) => {
    const s = q.status || 'draft';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const totalQuotations = quotations.reduce((sum, q) => sum + q.total, 0);

  if (loading) {
    return <div className="text-center py-12">Loading quotations...</div>;
  }

  return (
    <div data-testid="quotations-page">
      <div className="page-header">
        <h2 className="page-title">Quotations</h2>
        <button
          data-testid="add-quotation-button"
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Quotation
        </button>
      </div>

      {/* Approved Sales Quotations strip — Feb 2026 cross-module sync.
          Approved sales_quotations (status >= approved) appear here so the
          /quotations page becomes the single Quotations Hub the user sees. */}
      <ApprovedSalesQuotationsStrip />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon blue">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Quotations</h3>
          <p className="text-3xl font-bold text-gray-900" data-testid="quotations-total-count">{pageMeta.total}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Accepted</h3>
          <p className="text-3xl font-bold text-green-600">
            {quotations.filter(q => q.status === 'accepted').length}
          </p>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Value</h3>
          <p className="text-3xl font-bold text-purple-600">{totalQuotations.toLocaleString()} SAR</p>
        </div>
      </div>

      {/* Patch 21 — Server-side Search box. Resets to page 1 on
          submit and runs against the backend (quotation_no /
          customer_name / project_name / source_lead_no / source_rfq_no). */}
      <div className="flex items-center gap-2 mb-3" data-testid="quotations-search-bar">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
          <input
            data-testid="quotations-search-input"
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                setSearchQuery(searchInput.trim());
              }
            }}
            placeholder="Search QT-#, customer, project, lead, RFQ…"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-slate-500"
          />
        </div>
        <button
          data-testid="quotations-search-submit"
          onClick={() => { setPage(1); setSearchQuery(searchInput.trim()); }}
          className="px-3 py-1.5 text-xs font-semibold rounded bg-slate-900 hover:bg-slate-800 text-white"
        >
          Search
        </button>
        {searchQuery && (
          <button
            data-testid="quotations-search-clear"
            onClick={() => { setPage(1); setSearchInput(''); setSearchQuery(''); }}
            className="px-3 py-1.5 text-xs font-semibold rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Clear
          </button>
        )}
      </div>

      {/* Patch 18.1.1 — Approval Visibility Toggle.
          Drives the backend `status` filter so the operator can switch
          between the full operational view (default), CFO/finance-only
          approved view, and the in-flight pending queue. */}
      <div
        data-testid="quotation-approval-filter"
        className="flex items-center gap-2 mb-3"
      >
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Visibility:
        </span>
        {[
          { key: 'all',      label: 'All' },
          { key: 'pending',  label: 'Pending' },
          { key: 'approved', label: 'Approved' },
        ].map((opt) => (
          <button
            key={opt.key}
            data-testid={`approval-filter-${opt.key}`}
            onClick={() => { setPage(1); setApprovalFilter(opt.key); }}
            className={`px-3 py-1 rounded-md text-xs font-semibold border transition-colors ${
              approvalFilter === opt.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
        {/* Patch 21 — Server-side sort dropdown. Whitelist mirrors backend. */}
        <span className="ml-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Sort:
        </span>
        <select
          data-testid="quotations-sort-by"
          value={sortBy}
          onChange={(e) => { setPage(1); setSortBy(e.target.value); }}
          className="text-xs px-2 py-1 rounded border border-slate-300"
        >
          <option value="quotation_date">Date</option>
          <option value="quotation_no">Quotation #</option>
          <option value="customer_name">Customer</option>
          <option value="total_amount">Total</option>
          <option value="status">Status</option>
        </select>
        <button
          data-testid="quotations-sort-order"
          onClick={() => { setPage(1); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
          className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
          title="Toggle sort direction"
        >
          {sortOrder === 'desc' ? '↓ DESC' : '↑ ASC'}
        </button>
      </div>

      {/* Status Filter Chips — drives the state-machine slice of the list */}
      <div
        data-testid="quotation-status-filters"
        className="flex flex-wrap gap-2 mb-4"
      >
        {[
          { key: 'all', label: 'All', count: quotations.length },
          { key: 'draft', label: 'Draft', count: statusCounts.draft || 0 },
          { key: 'sent', label: 'Sent', count: statusCounts.sent || 0 },
          { key: 'accepted', label: 'Accepted', count: statusCounts.accepted || 0 },
          { key: 'converted', label: 'Converted', count: statusCounts.converted || 0 },
          { key: 'rejected', label: 'Rejected', count: statusCounts.rejected || 0 },
          { key: 'expired', label: 'Expired', count: statusCounts.expired || 0 },
        ].map(chip => (
          <button
            key={chip.key}
            data-testid={`filter-chip-${chip.key}`}
            onClick={() => setStatusFilter(chip.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              statusFilter === chip.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
            }`}
          >
            {chip.label} <span className="opacity-70">({chip.count})</span>
          </button>
        ))}
      </div>

      {/* Quotations Table - Split View */}
      <div className="flex gap-4">
        <div className={`table-container transition-all duration-300 ${selectedQuotation ? 'w-1/2' : 'w-full'}`}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Quotation No</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotations.length > 0 ? (
                filteredQuotations.map((quotation) => (
                  <tr 
                    key={quotation.id} 
                    data-testid={`quotation-row-${quotation.id}`}
                    onClick={() => setSelectedQuotation(selectedQuotation?.id === quotation.id ? null : quotation)}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedQuotation?.id === quotation.id ? 'bg-blue-100 border-l-4 border-blue-500' : ''}`}
                  >
                    <td className="font-semibold text-blue-600">
                      {quotation.quotation_no}
                      {(quotation.source_lead_id || quotation.source_rfq_id) && (
                        <span
                          data-testid={`source-badge-${quotation.id}`}
                          title={quotation.source_lead_no
                                  ? `Source: Lead ${quotation.source_lead_no}`
                                  : `Source: RFQ ${quotation.source_rfq_no}`}
                          className={`ml-1.5 inline-block text-[9px] font-bold px-1 py-0.5 rounded ${
                            quotation.source_lead_id
                              ? 'bg-sky-100 text-sky-800'
                              : 'bg-violet-100 text-violet-800'
                          }`}
                        >
                          📍 {quotation.source_lead_id
                                ? quotation.source_lead_no
                                : quotation.source_rfq_no}
                        </span>
                      )}
                    </td>
                    <td>{new Date(quotation.quotation_date).toLocaleDateString()}</td>
                    <td className="font-medium">{quotation.customer_name}</td>
                    <td className="font-bold">{quotation.total.toLocaleString()} SAR</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(quotation.status)}`}>
                        {quotation.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => downloadPDF(quotation)}
                          className="btn-icon"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4 text-blue-600" />
                        </button>
                        {canDelete(quotation) && (
                          <button
                            data-testid={`delete-quotation-${quotation.id}`}
                            onClick={() => handleDelete(quotation.id)}
                            className="btn-icon delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-500">
                    No quotations found. Create your first quotation!
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Patch 21 — Server-side pagination bar. Shows the current
              page, the total page count, and prev / next navigation.
              For larger result sets a 7-wide windowed selector is
              rendered so the operator can jump to any nearby page. */}
          {pageMeta.total_pages > 1 && (
            <div
              className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between"
              data-testid="quotations-pagination"
            >
              <div className="text-xs text-gray-500" data-testid="quotations-pagination-status">
                Page <span className="font-mono font-semibold text-gray-900">{page}</span>
                {' '}of <span className="font-mono font-semibold text-gray-900">{pageMeta.total_pages}</span>
                {' '}— <span className="font-mono font-semibold text-gray-900">{pageMeta.total}</span> total
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="quotations-page-prev"
                  disabled={page <= 1}
                  onClick={() => setPage(Math.max(1, page - 1))}
                  className="px-2 py-1 text-xs rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
                >
                  ‹ Prev
                </button>
                {(() => {
                  // Windowed page numbers: keep the page chip visible
                  // even on huge result sets. Always show 1 and last.
                  const tp = pageMeta.total_pages;
                  const window_ = 5;
                  const start = Math.max(2, page - Math.floor(window_ / 2));
                  const end   = Math.min(tp - 1, start + window_ - 1);
                  const realStart = Math.max(2, Math.min(start, end - window_ + 1));
                  const pages = [];
                  pages.push(1);
                  if (realStart > 2) pages.push('…');
                  for (let i = realStart; i <= end; i += 1) pages.push(i);
                  if (end < tp - 1) pages.push('…');
                  if (tp > 1) pages.push(tp);
                  return pages.map((p, i) => (
                    p === '…' ? (
                      <span key={`gap-${i}`} className="px-1 text-xs text-slate-400">…</span>
                    ) : (
                      <button
                        key={p}
                        data-testid={`quotations-page-${p}`}
                        onClick={() => setPage(p)}
                        className={`min-w-[2rem] px-2 py-1 text-xs rounded border font-mono ${
                          p === page
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  ));
                })()}
                <button
                  data-testid="quotations-page-next"
                  disabled={page >= pageMeta.total_pages}
                  onClick={() => setPage(Math.min(pageMeta.total_pages, page + 1))}
                  className="px-2 py-1 text-xs rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
                >
                  Next ›
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Quotation Details Panel */}
        {selectedQuotation && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedQuotation.quotation_no}</h3>
                <p className="text-sm text-gray-500">Quotation Details</p>
              </div>
              <div className="flex items-center gap-2">
                {/* Patch 22 — open Sales Quotation PDF in new tab.
                    The endpoint streams `inline` so the browser
                    previews it; user can Save-As to download. */}
                <button
                  onClick={() => {
                    axiosInstance
                      .get(`/quotations/${selectedQuotation.id}/pdf`, { responseType: 'blob' })
                      .then((r) => {
                        const url = URL.createObjectURL(r.data);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60_000);
                      })
                      .catch(() => toast.error('PDF generation failed'));
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 transition-colors"
                  data-testid="quotation-download-pdf-btn"
                  title="Open the customer-facing Sales Quotation PDF"
                >
                  <Download className="w-3.5 h-3.5" />
                  PDF
                </button>
                <button
                  onClick={() => {
                    axiosInstance
                      .get(`/quotations/${selectedQuotation.id}/narrative-pdf`, { responseType: 'blob' })
                      .then((r) => {
                        const url = URL.createObjectURL(r.data);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60_000);
                      })
                      .catch(() => toast.error('Narrative PDF generation failed'));
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 transition-colors"
                  data-testid="quotation-narrative-pdf-btn"
                  title="Open the Decision Narrative PDF (audit-grade chronology)"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Narrative PDF
                </button>
                <button
                  onClick={() => {
                    axiosInstance
                      .get(`/quotations/${selectedQuotation.id}/audit-pack-pdf`, { responseType: 'blob' })
                      .then((r) => {
                        const url = URL.createObjectURL(r.data);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60_000);
                      })
                      .catch(() => toast.error('Audit Pack PDF generation failed'));
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 transition-colors"
                  data-testid="quotation-audit-pack-btn"
                  title="Open the Audit Evidence Pack PDF (complete auditor packet)"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Audit Pack
                </button>
                <button
                  onClick={() => setShowTimeline(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors"
                  data-testid="open-unified-timeline-button"
                  title="Open the unified Decision Narrative for this quotation"
                >
                  <History className="w-3.5 h-3.5" />
                  Timeline
                </button>
                <button
                  onClick={() => setSelectedQuotation(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Contract Risk Engine — the consolidated risk surface
                that fuses signals from Revision/Behavior/Invoice
                history. Rendered ABOVE the approval banner so the CFO
                sees the risk picture BEFORE approving. */}
            {/* Contract Freeze re-approval signal — visible when a new
                revision was just forked off a previously-approved row.
                Lets the user know the prior approval is dead and a new
                one must be obtained before convert-to-invoice. */}
            {selectedQuotation.requires_reapproval && (
              <div
                data-testid="reapproval-required-banner"
                className="mb-3 p-3 rounded-lg border border-orange-300 bg-orange-50 text-sm"
              >
                <div className="flex items-center gap-2 font-semibold text-orange-900">
                  🧊 Re-Approval Required
                </div>
                <div className="text-xs text-orange-800 mt-1">
                  Previous approved: <span className="font-mono font-semibold">
                    {selectedQuotation.previous_approved_revision_no}
                  </span>
                  {selectedQuotation.previous_approved_total != null && (
                    <> · {Number(selectedQuotation.previous_approved_total).toLocaleString()} SAR</>
                  )}
                  <br />
                  This revision must be re-routed through the approval matrix before convert.
                </div>
              </div>
            )}

            <ContractRiskCard quotation={selectedQuotation} />

            {/* P3 — Velocity Alerts (anomaly surface). Renders ONLY
                when an alert exists; otherwise zero visual noise. */}
            <VelocityAlertsStrip quotationId={selectedQuotation.id} />

            {/* P2 — Pricing Recommendation (advisory only). Surfaces
                negotiation buffer / discount ceiling / advance payment
                etc. based on the customer's FROZEN contract history. */}
            <PricingRecommendationCard quotationId={selectedQuotation.id} />

            {/* Decision Routing — base tier + risk-driven escalations,
                rendered between Risk and Approval banner so the user
                sees the routing reasoning before clicking Request Approval. */}
            <RoutingDecisionCard quotation={selectedQuotation} />

            {/* Approval Matrix banner — surfaces the value-tier
                requirement BEFORE the revision banner. Hidden for
                amounts in the auto-approved tier. */}
            <QuotationApprovalBanner
              quotation={selectedQuotation}
              onApprovalRequested={() => fetchData()}
              onApprovalDecided={() => fetchData()}
            />

            {/* Multi-Step Approval Chain Timeline — drives the
                step-by-step approval flow. Shows up only once a chain
                exists for this quotation; the "Start Approval Chain"
                button is offered when the matrix would require one. */}
            {approvalChain ? (
              <ApprovalChainTimeline
                chain={approvalChain}
                currentUserRole={user?.role}
                currentUserId={user?.id}
                onDecide={async (decision, note) => {
                  try {
                    await axiosInstance.post(
                      `/quotations/${selectedQuotation.id}/approval-chain/decide`,
                      { decision, note },
                    );
                    toast.success(`Step ${decision}d`);
                    const r = await axiosInstance.get(
                      `/quotations/${selectedQuotation.id}/approval-chain`,
                    );
                    setApprovalChain(r?.data?.chain || null);
                    fetchData();
                  } catch (e) {
                    const detail = e?.response?.data?.detail;
                    toast.error(
                      (detail && (detail.error || detail.message)) ||
                      'Failed to record decision',
                    );
                  }
                }}
              />
            ) : (
              (governance?.is_latest !== false &&
               !['converted', 'rejected', 'expired', 'closed']
                  .includes((selectedQuotation.status || '').toLowerCase())) && (
                <div className="mb-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/40 flex items-center justify-between gap-2"
                     data-testid="chain-start-banner">
                  <span className="text-sm text-indigo-900">
                    Start the Multi-Step Approval Chain to route this
                    quotation through every required approver.
                  </span>
                  <button
                    data-testid="start-approval-chain-button"
                    onClick={async () => {
                      try {
                        await axiosInstance.post(
                          `/quotations/${selectedQuotation.id}/approval-chain/start`,
                        );
                        toast.success('Approval chain started');
                        const r = await axiosInstance.get(
                          `/quotations/${selectedQuotation.id}/approval-chain`,
                        );
                        setApprovalChain(r?.data?.chain || null);
                        fetchData();
                      } catch (e) {
                        const detail = e?.response?.data?.detail;
                        toast.error(
                          (detail && (detail.error || detail.message)) ||
                          'Failed to start chain',
                        );
                      }
                    }}
                    className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
                    Start Chain
                  </button>
                </div>
              )
            )}

            {/* Revision Chain Banner — Versioned Commercial Contract. */}
            {(governance?.revision_number > 0 || (revisions?.count || 0) > 1) && (
              <div
                data-testid="quotation-revision-banner"
                className={`mb-3 p-3 rounded-lg border text-sm flex items-center justify-between gap-2 ${
                  governance?.is_latest === false
                    ? 'bg-gray-50 border-gray-300 text-gray-700'
                    : 'bg-indigo-50 border-indigo-200 text-indigo-900'
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <GitBranch className="w-4 h-4" />
                  <span className="font-semibold">
                    Revision R{governance?.revision_number ?? selectedQuotation.revision_number ?? 0}
                  </span>
                  {revisions?.count > 1 && (
                    <span className="opacity-75">of {revisions.count}</span>
                  )}
                  {governance?.is_latest === false && (
                    <span className="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                      <Lock className="w-3 h-3" /> Superseded — read-only
                    </span>
                  )}
                  {governance?.is_latest && governance?.revision_number > 0 && (
                    <span className="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                      Latest
                    </span>
                  )}
                  {/* Contract Integrity Layer — customer lock chip + content fingerprint. */}
                  <span
                    data-testid="contract-customer-lock-chip"
                    title={`Customer is locked across this contract chain — ${selectedQuotation?.customer_name || ''}`}
                    className="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-slate-900 text-slate-100 border border-slate-700"
                  >
                    <Lock className="w-3 h-3" />
                    Customer Locked
                  </span>
                  {selectedQuotation?.revision_hash && (
                    <span
                      data-testid="contract-revision-hash-chip"
                      title={`Contract content fingerprint (SHA-256): ${selectedQuotation.revision_hash}`}
                      className="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-200 font-mono"
                    >
                      <ShieldCheck className="w-3 h-3" />
                      {String(selectedQuotation.revision_hash).slice(0, 12)}
                    </span>
                  )}
                </div>
                {revisions?.count > 1 && (
                  <button
                    data-testid="toggle-revision-history-button"
                    onClick={() => setShowRevisionHistory(v => !v)}
                    className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-current opacity-90 hover:opacity-100"
                  >
                    <History className="w-3 h-3" />
                    {showRevisionHistory ? 'Hide' : 'Show'} History
                  </button>
                )}
              </div>
            )}

            {/* "Why did this change?" — behavioral classifier for the
                currently-selected revision row. R0 / no-data is silent. */}
            <RevisionExplainerCard quotation={selectedQuotation} />

            {/* Revision History Viewer — full chain, R0 → Rn. */}
            {showRevisionHistory && revisions?.revisions?.length > 1 && (
              <div
                data-testid="quotation-revision-history"
                className="mb-4 border border-gray-200 rounded-lg overflow-hidden text-sm"
              >
                <div className="bg-gray-50 px-3 py-2 font-semibold text-gray-700 border-b">
                  Revision History · {revisions.count} versions
                </div>
                <div className="divide-y">
                  {revisions.revisions.map((rev) => {
                    const isCurrent = rev.id === selectedQuotation.id;
                    return (
                      <button
                        key={rev.id}
                        data-testid={`revision-row-${rev.quotation_no}`}
                        onClick={() => setSelectedQuotation(rev)}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${
                          isCurrent ? 'bg-indigo-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono font-semibold text-gray-900">
                            {rev.quotation_no}
                          </span>
                          {rev.is_latest && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                              latest
                            </span>
                          )}
                          {rev.revision_reason && (
                            <span className="text-xs text-gray-500 truncate" title={rev.revision_reason}>
                              · {rev.revision_reason}
                            </span>
                          )}
                        </div>
                        <span className="font-mono text-xs text-gray-600 flex-shrink-0">
                          {Number(rev.total || 0).toLocaleString()} SAR
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Status badge + State-machine action buttons */}
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span
                  data-testid="quotation-status-badge"
                  className={`status-badge ${getStatusColor(selectedQuotation.status)} text-sm`}
                >
                  {(selectedQuotation.status || 'draft').toUpperCase()}
                </span>
                {governance?.locked && (
                  <span
                    data-testid="quotation-locked-badge"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                    title="This quotation is an immutable financial snapshot."
                  >
                    🔒 Immutable Snapshot
                  </span>
                )}
                <button
                  data-testid="download-pdf-button"
                  onClick={() => downloadPDF(selectedQuotation)}
                  className="btn-primary text-sm py-1 px-3"
                >
                  <Download className="w-4 h-4 inline mr-1" />
                  PDF
                </button>
              </div>

              {/* State-machine actions — only the transitions allowed for
                  the current state appear. Backend enforces idempotency
                  and rejects illegal moves with 409. */}
              <div className="flex flex-wrap gap-2">
                {(STATE_MACHINE[selectedQuotation.status || 'draft'] || []).map(target => {
                  const meta = {
                    sent:     { icon: Send,        cls: 'bg-blue-600 hover:bg-blue-700', label: 'Mark as Sent' },
                    accepted: { icon: CheckCircle2, cls: 'bg-green-600 hover:bg-green-700', label: 'Mark as Accepted' },
                    rejected: { icon: XCircle,     cls: 'bg-red-600 hover:bg-red-700',   label: 'Reject' },
                    expired:  { icon: Clock,       cls: 'bg-gray-600 hover:bg-gray-700', label: 'Mark Expired' },
                  }[target];
                  if (!meta) return null;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={target}
                      data-testid={`transition-${target}-button`}
                      onClick={() => transitionTo(selectedQuotation, target)}
                      className={`${meta.cls} text-white text-sm px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors`}
                    >
                      <Icon className="w-4 h-4" />
                      {meta.label}
                    </button>
                  );
                })}

                {/* Edit Session — Controlled Mutation Layer.
                    Opens a session whose patches accumulate server-side
                    and apply as a brand-new revision. The original row
                    is NEVER overwritten. Gated by governance.can_revise
                    so the UI mirrors the server's contract. */}
                {ROLE_CAN_REVISE && governance?.can_revise && (
                  <button
                    data-testid="open-edit-session-button"
                    onClick={() => setShowEditSession(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors font-semibold"
                    title="Open a controlled edit session — changes apply as a new revision."
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                )}

                {/* Revise — fork a new revision (R+1) off the current
                    quotation. Allowed roles: admin / accountant / sales_rep.
                    Hidden when the chain is terminal OR this row is not
                    the latest. Server validates regardless. */}
                {ROLE_CAN_REVISE && governance?.can_revise && (
                  <button
                    data-testid="revise-quotation-button"
                    onClick={() => handleRevise(selectedQuotation)}
                    className="bg-amber-500 hover:bg-amber-600 text-white text-sm px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors font-semibold"
                    title="Create a new revision (R+1). Original becomes read-only."
                  >
                    <GitBranch className="w-4 h-4" />
                    Revise (R{(governance?.revision_number ?? 0) + 1})
                  </button>
                )}

                {/* Convert to Invoice — financial action, accountant + admin only.
                    Server enforces via 403 NOT_ALLOWED_TO_CONVERT_QUOTATION.
                    UI HIDES the button (per spec: hidden, not disabled) when
                    either (a) role is not authorised, or (b) the live
                    governance contract from the server returns can_convert=false. */}
                {ROLE_CAN_CONVERT
                  && governance?.can_convert
                  && ['draft', 'sent', 'accepted'].includes(selectedQuotation.status || 'draft') && (
                  <button
                    data-testid="convert-to-invoice-button"
                    disabled={converting}
                    onClick={() => handleConvertToInvoice(selectedQuotation)}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors font-semibold"
                  >
                    <Receipt className="w-4 h-4" />
                    {converting ? 'Converting…' : 'Convert to Invoice'}
                  </button>
                )}

                {/* Read-only signal for roles that cannot convert — preserves
                    the "controlled financial system" UX contract. */}
                {!ROLE_CAN_CONVERT && (
                  <span
                    data-testid="quotation-view-only-badge"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200"
                    title="Only accountants and admins may convert a quotation to an invoice."
                  >
                    View-only · Accounting role required to convert
                  </span>
                )}
              </div>
            </div>

            {/* Traceability — link to the resulting invoice once converted */}
            {selectedQuotation.status === 'converted' && selectedQuotation.converted_invoice_id && (
              <div
                data-testid="quotation-conversion-link"
                className="mb-4 p-3 rounded-lg border border-indigo-200 bg-indigo-50 text-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-indigo-900">Converted to Invoice</div>
                    <div className="text-indigo-700">
                      {selectedQuotation.converted_invoice_number}
                      {selectedQuotation.converted_at && (
                        <span className="text-gray-500"> · {new Date(selectedQuotation.converted_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  <a
                    href={`/invoices?focus=${selectedQuotation.converted_invoice_id}`}
                    className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 font-medium"
                  >
                    Open Invoice <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}

            {/* Customer Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Customer Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <p className="font-medium">{selectedQuotation.customer_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Attention To:</span>
                  <p className="font-medium">{selectedQuotation.attention_to || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Address:</span>
                  <p className="font-medium">{selectedQuotation.customer_address || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">VAT Number:</span>
                  <p className="font-medium">{selectedQuotation.customer_vat_number || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Quotation Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Quotation Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Date:</span>
                  <p className="font-medium">{new Date(selectedQuotation.quotation_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Valid Until:</span>
                  <p className="font-medium">{new Date(selectedQuotation.valid_date).toLocaleDateString()}</p>
                </div>
                {selectedQuotation.project_name && (
                  <div>
                    <span className="text-gray-500">Project:</span>
                    <p className="font-medium">{selectedQuotation.project_name}</p>
                  </div>
                )}
                {selectedQuotation.subject && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Subject:</span>
                    <p className="font-medium">{selectedQuotation.subject}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Items</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-center">Qty</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedQuotation.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 text-center">{item.quantity} {item.unit}</td>
                        <td className="px-3 py-2 text-right">{item.unit_price?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-medium">{item.total?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Subtotal:</span>
                <span>{selectedQuotation.subtotal?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>VAT ({selectedQuotation.vat_rate || 15}%):</span>
                <span>{selectedQuotation.vat_amount?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                <span>Total:</span>
                <span className="text-blue-600">{selectedQuotation.total?.toLocaleString()} SAR</span>
              </div>
            </div>

            {/* Notes */}
            {selectedQuotation.notes && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Notes</h4>
                <p className="text-sm text-gray-600">{selectedQuotation.notes}</p>
              </div>
            )}

            {/* Created By */}
            <div className="mt-4 text-xs text-gray-400">
              Created by: {selectedQuotation.user_name || 'N/A'}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-6xl w-full max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New Quotation</h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Quotation Date *</label>
                  <input
                    data-testid="quotation-date-input"
                    type="date"
                    value={formData.quotation_date}
                    onChange={(e) => setFormData({ ...formData, quotation_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Valid Until *</label>
                  <input
                    data-testid="valid-date-input"
                    type="date"
                    value={formData.valid_date}
                    onChange={(e) => setFormData({ ...formData, valid_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Project (Optional)</label>
                  <select
                    data-testid="project-select"
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    className="form-select"
                  >
                    <option value="">No Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Customer Selection */}
              <div className="form-group mb-4">
                <label className="form-label">Customer *</label>
                <select
                  data-testid="customer-select"
                  value={formData.customer_id}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name_english || customer.name_arabic}
                    </option>
                  ))}
                </select>
              </div>

              {/* Customer Details (Auto-filled) */}
              {selectedCustomer && (
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Customer Details</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Address:</span>
                      <p className="font-medium">{selectedCustomer.address || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">CR Number:</span>
                      <p className="font-medium">{selectedCustomer.cr_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">VAT Number:</span>
                      <p className="font-medium">{selectedCustomer.vat_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Attention To:</span>
                      <input
                        type="text"
                        value={formData.attention_to}
                        onChange={(e) => setFormData({ ...formData, attention_to: e.target.value })}
                        className="form-input text-sm mt-1"
                        placeholder="Contact person"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div className="form-group mb-4">
                <label className="form-label">Subject *</label>
                <input
                  data-testid="subject-input"
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="form-input"
                  placeholder="Quotation subject"
                  required
                />
              </div>

              {/* Items */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-3">
                  <label className="form-label mb-0">Items *</label>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid="open-vendor-import-button"
                      type="button"
                      onClick={() => setShowVendorImport(true)}
                      className="text-indigo-600 hover:text-indigo-700 font-medium text-sm flex items-center gap-1"
                      title="Pull items from a Vendor Quotation"
                    >
                      <Package className="w-4 h-4" />
                      Import from Vendor Quotation
                    </button>
                    <button
                      type="button"
                      onClick={addItem}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1"
                    >
                      <PlusCircle className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center w-16">S.No</th>
                        <th className="px-3 py-2 text-left">Description *</th>
                        <th className="px-3 py-2 text-center" style={{ width: '150px' }}>Qty</th>
                        <th className="px-3 py-2 text-center w-28">Unit</th>
                        <th className="px-3 py-2 text-right w-32">Unit Price</th>
                        <th className="px-3 py-2 text-right w-32">Total</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-3 py-2 text-center font-medium text-gray-600">
                            {index + 1}
                          </td>
                          <td className="px-3 py-2">
                            {/* When an item carries a free-text description but no
                                inventory_id (imported from a Vendor Quotation),
                                render an editable text input so the user can
                                refine it before sending. Otherwise stay on the
                                inventory-driven dropdown. */}
                            {item.description && !item.inventory_id ? (
                              <div className="space-y-1">
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                  className="form-input text-sm"
                                  placeholder="Item description"
                                  required
                                  data-testid={`item-description-input-${index}`}
                                />
                                {item.source_vendor_quote_no && (
                                  <p className="text-[11px] text-indigo-600 flex items-center gap-1">
                                    <Package className="w-3 h-3" />
                                    From {item.source_vendor_quote_no}
                                    {item.source_supplier_name ? ` · ${item.source_supplier_name}` : ''}
                                  </p>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleItemChange(index, 'inventory_id', '')}
                                  className="text-[11px] text-gray-500 hover:text-blue-600 underline"
                                >
                                  Switch to inventory item
                                </button>
                              </div>
                            ) : (
                              <select
                                value={item.inventory_id || ''}
                                onChange={(e) => handleItemChange(index, 'inventory_id', e.target.value)}
                                className="form-select text-sm"
                                required
                              >
                                <option value="">Select Item</option>
                                {inventory.map(inv => (
                                  <option key={inv.id} value={inv.id}>
                                    {inv.name || inv.item_name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              className="form-input text-lg text-center w-full py-3 font-bold bg-yellow-50 border-2 border-yellow-300 rounded-lg"
                              min="0"
                              step="0.01"
                              style={{ minWidth: '130px', fontSize: '18px', height: '50px' }}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={item.unit}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              className="form-select text-sm"
                            >
                              <option value="unit">Unit</option>
                              <option value="pcs">Pcs</option>
                              <option value="m">m</option>
                              <option value="m2">m²</option>
                              <option value="m3">m³</option>
                              <option value="kg">kg</option>
                              <option value="ton">Ton</option>
                              <option value="hr">Hour</option>
                              <option value="day">Day</option>
                              <option value="lot">Lot</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="form-input text-sm text-right"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {item.total.toFixed(2)} SAR
                          </td>
                          <td className="px-3 py-2">
                            {formData.items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="mt-4 flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">{formData.subtotal.toFixed(2)} SAR</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">VAT:</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={formData.vat_rate}
                          onChange={(e) => handleVatChange(parseFloat(e.target.value) || 0)}
                          className="form-input w-16 text-sm text-center"
                          min="0"
                          max="100"
                        />
                        <span>%</span>
                        <span className="font-medium">{formData.vat_amount.toFixed(2)} SAR</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total:</span>
                      <span className="text-blue-600">{formData.total.toFixed(2)} SAR</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment & Delivery Terms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Payment Terms</label>
                  <input
                    type="text"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="form-input"
                    placeholder="e.g., 50% advance, 50% upon completion"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Terms</label>
                  <input
                    type="text"
                    value={formData.delivery_terms}
                    onChange={(e) => setFormData({ ...formData, delivery_terms: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Within 30 days from order confirmation"
                  />
                </div>
              </div>

              {/* Bank Details */}
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-blue-800 mb-3">Bank Details</h4>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 block">Bank Name</span>
                    <span className="font-medium">ALAHLI BANK (SNB)</span>
                  </div>
                  <div>
                    <span className="text-gray-600 block">Account</span>
                    <span className="font-medium">24700000524008</span>
                  </div>
                  <div>
                    <span className="text-gray-600 block">IBAN</span>
                    <span className="font-medium">SA6910000024700000524008</span>
                  </div>
                  <div>
                    <span className="text-gray-600 block">SWIFT</span>
                    <span className="font-medium">NCBSAJE</span>
                  </div>
                </div>
              </div>

              {/* Terms & Notes */}
              <div className="grid grid-cols-1 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Terms & Conditions</label>
                  <textarea
                    value={formData.terms}
                    onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                    className="form-input font-mono text-xs"
                    rows="10"
                    placeholder="Payment terms, delivery conditions..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="form-input"
                    rows="2"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div
                data-testid="quotation-form-footer"
                className="flex flex-col gap-3 mt-6 pt-4 border-t border-gray-200"
              >
                <div className="text-xs text-gray-500 flex items-start gap-1.5">
                  <Save className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Tip:</strong> Save as Draft to keep editing later. The draft will appear
                    in the list with a DRAFT badge — click it to re-open and refine before sending
                    to the customer. Once sent, further changes must go through <em>Revise</em>.
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    data-testid="save-as-draft-button"
                    type="button"
                    disabled={saving}
                    onClick={(e) => handleSubmit(e, { sendAfterSave: false })}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-gray-300 text-gray-800 font-medium px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving…' : 'Save as Draft'}
                  </button>
                  <button
                    data-testid="save-and-send-button"
                    type="submit"
                    disabled={saving}
                    onClick={(e) => handleSubmit(e, { sendAfterSave: true })}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    {saving ? 'Sending…' : 'Save & Send to Customer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Controlled Mutation Layer — opens against the currently-
          selected quotation. Closing or applying disposes the session. */}
      {showEditSession && selectedQuotation && (
        <QuotationEditSessionModal
          quotation={selectedQuotation}
          onClose={() => setShowEditSession(false)}
          onApplied={(newRev) => {
            // Switch the detail panel to the freshly-created revision
            // and refresh the list so the obsolete row disappears.
            setSelectedQuotation(newRev);
            fetchData();
          }}
        />
      )}

      {/* Vendor Quotation → Customer Quotation importer.
          Opened from inside the +New Quotation modal; passes the active
          project (if any) so the picker can filter the supplier list. */}
      {showVendorImport && (
        <VendorQuoteImportModal
          projectId={formData.project_id}
          projectName={
            (projects.find((p) => p.id === formData.project_id) || {}).name
          }
          onClose={() => setShowVendorImport(false)}
          onImport={handleVendorImport}
        />
      )}

      {/* Patch 18.1 — Unified Decision Narrative Drawer. Composes the
          full storyline (Lead/RFQ → Quotation → Approvals → Project →
          Invoices → Journals → Audit) from a single backend endpoint
          so every actor sees the same chronological truth. */}
      {selectedQuotation && (
        <UnifiedQuotationTimeline
          quotationId={selectedQuotation.id}
          open={showTimeline}
          onClose={() => setShowTimeline(false)}
        />
      )}

    </div>
  );
};

export default Quotations;


// ─────────────── Approved Sales Quotations Strip ───────────────
// Surfaces sales_quotations once they reach `approved` (or beyond)
// inside the legacy /quotations page so that an approved customer
// quote shows up where managers normally look for them.
function ApprovedSalesQuotationsStrip() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get('/sales-quotations?limit=200');
        const list = (r.data?.quotations || [])
          .filter((q) => ['approved', 'sent_to_employee', 'sent_to_customer', 'customer_accepted']
                          .includes(q.status))
          .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
        setRows(list);
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, []);

  if (loading || rows.length === 0) return null;

  const tone = (st) => ({
    approved:          'bg-teal-200 text-teal-900',
    sent_to_employee:  'bg-indigo-200 text-indigo-900',
    sent_to_customer:  'bg-green-600 text-white',
    customer_accepted: 'bg-emerald-700 text-white',
  })[st] || 'bg-gray-200 text-gray-800';

  return (
    <div className="mb-6 border-2 border-emerald-200 bg-emerald-50/40 rounded-xl overflow-hidden"
          data-testid="approved-sales-quotations-strip">
      <div className="px-4 py-2.5 bg-emerald-100 border-b border-emerald-200 flex items-center justify-between">
        <h2 className="text-sm font-bold text-emerald-900 inline-flex items-center gap-2">
          ✅ Approved Sales Quotations · <span className="font-mono">{rows.length}</span>
        </h2>
        <a href="/sales-quotations" className="text-[11px] text-emerald-800 hover:underline">
          Open full Sales Quotations workspace →
        </a>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white text-gray-600 text-xs">
            <tr>
              <th className="text-left px-3 py-2">Quote #</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Project</th>
              <th className="text-left px-3 py-2">Quote Date</th>
              <th className="text-left px-3 py-2">Expiry</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Linked Contract</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => (
              <tr key={q.id} className="border-t border-emerald-100 hover:bg-emerald-50/60"
                   data-testid={`approved-sq-row-${q.quote_no}`}>
                <td className="px-3 py-2 font-mono text-indigo-700 font-semibold">
                  {q.quote_no}
                  {(q.version || 1) > 1 && (
                    <span className="ml-1 text-[9px] px-1 rounded bg-purple-100 text-purple-800 font-semibold align-middle">
                      v{q.version}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">{q.customer_name || '—'}</td>
                <td className="px-3 py-2">{q.project_name || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{q.quote_date || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{q.expiry_date || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{(q.total || 0).toFixed(2)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${tone(q.status)}`}>
                    {q.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {q.contract_number ? (
                    <a href="/contracts" className="text-emerald-700 font-semibold hover:underline">
                      {q.contract_number} →
                    </a>
                  ) : <span className="text-gray-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
